import type { TFunction } from "i18next";
import type {
  PartnerRevenueAnalyticsApiPoint,
  PartnerRevenueAnalyticsApiResponse,
  PartnerRevenueAnalyticsBucket,
  PartnerRevenueAnalyticsChartPoint,
} from "@/types/partner-revenue-analytics";
import type { PartnerRevenueDateRange } from "@/components/pages/guesteria-partners/utils/partnerRevenuePeriodPresets";

export type PartnerAnalyticsPeriodParams = {
  from: string;
  to: string;
  bucket: PartnerRevenueAnalyticsBucket;
};

export function toDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Task 0038 (owner, 2026-09-30): period (the shared `PartnerPeriodSelector`
 * range, future-inclusive) and chart granularity ("Group by") are now
 * separate controls. Orders/summary use the range as-is — future
 * ("Expected") bookings must stay reachable. The trend chart alone is cut
 * at today (decision: clamp + a static "Expected" caption, over greyed
 * future buckets, to avoid any charting-library-specific work).
 */
export function getPartnerRangeParams(range: PartnerRevenueDateRange): {
  from: string;
  to: string;
} {
  return { from: toDateParam(range.from), to: toDateParam(range.to) };
}

export function getPartnerAnalyticsRangeParams(
  range: PartnerRevenueDateRange,
  bucket: PartnerRevenueAnalyticsBucket,
  referenceDate: Date = new Date(),
): PartnerAnalyticsPeriodParams {
  const clampedTo =
    range.to.getTime() > referenceDate.getTime() ? referenceDate : range.to;
  const to =
    clampedTo.getTime() < range.from.getTime() ? range.from : clampedTo;
  return { from: toDateParam(range.from), to: toDateParam(to), bucket };
}

/** Whether the chart for this range was clamped — the "Expected" caption. */
export function isPartnerRevenueRangeBeyondToday(
  range: PartnerRevenueDateRange,
  referenceDate: Date = new Date(),
): boolean {
  return range.to.getTime() > referenceDate.getTime();
}

function formatDayLabel(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatWeekLabel(date: Date, locale: string, t: TFunction): string {
  return t("partners.detail.weekOfLabel", {
    date: formatDayLabel(date, locale),
  });
}

function formatMonthLabel(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatPartnerAnalyticsPointLabel(
  dateIso: string,
  bucket: PartnerRevenueAnalyticsBucket,
  locale: string,
  t: TFunction,
): string {
  const date = new Date(dateIso);

  switch (bucket) {
    case "week":
      return formatWeekLabel(date, locale, t);
    case "month":
      return formatMonthLabel(date, locale);
    case "day":
    default:
      return formatDayLabel(date, locale);
  }
}

export function mapPartnerRevenueAnalyticsToChartPoints(
  response: PartnerRevenueAnalyticsApiResponse,
  locale: string,
  t: TFunction,
): PartnerRevenueAnalyticsChartPoint[] {
  return response.points.map((point: PartnerRevenueAnalyticsApiPoint) => ({
    label: formatPartnerAnalyticsPointLabel(
      point.date,
      response.bucket,
      locale,
      t,
    ),
    totalRevenue: point.totalRevenue,
    booked: point.booked ?? 0,
    totalOrders: point.totalOrders,
    currency: point.currency,
  }));
}
